import { useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { getArticleById, getRelatedArticles } from '@/mocks/article';

function ScrollToTop() {
  const { id } = useParams();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);
  return null;
}

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = id ? parseInt(id, 10) : 0;

  if (!articleId || isNaN(articleId)) {
    return <Navigate to="/" replace />;
  }

  const article = getArticleById(articleId);
  if (!article) {
    return <Navigate to="/" replace />;
  }

  const related = getRelatedArticles(article.relatedIds);

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        {/* Breadcrumb */}
        <div className="max-w-5xl mx-auto px-4 md:px-6 pt-6">
          <nav className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <Link to={`/category/${article.tags.includes('动画') ? 'anime' : article.tags.includes('科幻') ? 'tv' : 'movie'}`} className="hover:text-primary-500 transition-colors cursor-pointer">
              {article.tags[0]}
            </Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-800 font-medium truncate">{article.title}</span>
          </nav>
        </div>

        {/* Hero banner */}
        <section className="relative w-full h-[300px] md:h-[420px] mt-4 rounded-none overflow-hidden">
          <div className="max-w-5xl mx-auto px-4 md:px-6 h-full">
            <div className="relative w-full h-full rounded-xl overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-black/10"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  {article.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 text-xs font-medium rounded-full bg-white/15 backdrop-blur-sm text-white border border-white/20">
                      {tag}
                    </span>
                  ))}
                  <span className="text-sm text-white/70">{article.year}</span>
                </div>
                <h1 className="text-2xl md:text-4xl font-heading font-bold text-white mb-2">
                  {article.title}
                </h1>
                <p className="text-sm md:text-base text-white/70 max-w-2xl">
                  {article.subtitle}
                </p>
                <div className="flex items-center gap-4 mt-4">
                  <div className="flex items-center gap-1">
                    <i className="ri-star-fill text-accent-500 text-xl"></i>
                    <span className="text-white font-bold text-lg">{article.rating}</span>
                    <span className="text-white/50 text-sm">/ 10</span>
                  </div>
                  <span className="text-white/50 text-sm">{article.updateInfo}</span>
                  <span className="text-white/50 text-sm hidden sm:inline">{article.episodeInfo}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Main content */}
              <div className="lg:col-span-2">
                {/* Basic info */}
                <div className="bg-background-100 rounded-xl p-6 mb-6">
                  <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">影片信息</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-3 gap-x-6">
                    <div>
                      <span className="text-xs text-foreground-500">导演</span>
                      <p className="text-sm font-medium text-foreground-900 mt-0.5">{article.director}</p>
                    </div>
                    <div>
                      <span className="text-xs text-foreground-500">主演</span>
                      <p className="text-sm font-medium text-foreground-900 mt-0.5">{article.cast.join('、')}</p>
                    </div>
                    <div>
                      <span className="text-xs text-foreground-500">地区</span>
                      <p className="text-sm font-medium text-foreground-900 mt-0.5">{article.region}</p>
                    </div>
                    <div>
                      <span className="text-xs text-foreground-500">语言</span>
                      <p className="text-sm font-medium text-foreground-900 mt-0.5">{article.language}</p>
                    </div>
                    <div>
                      <span className="text-xs text-foreground-500">时长</span>
                      <p className="text-sm font-medium text-foreground-900 mt-0.5">{article.duration}</p>
                    </div>
                    <div>
                      <span className="text-xs text-foreground-500">类型</span>
                      <p className="text-sm font-medium text-foreground-900 mt-0.5">{article.category}</p>
                    </div>
                  </div>
                </div>

                {/* Synopsis */}
                <div className="bg-background-100 rounded-xl p-6 mb-6">
                  <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">剧情简介</h2>
                  <p className="text-sm text-foreground-700 leading-relaxed whitespace-pre-line">
                    {article.synopsis}
                  </p>
                </div>

                {/* Highlights */}
                <div className="bg-background-100 rounded-xl p-6 mb-6">
                  <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">影片看点</h2>
                  <ul className="space-y-3">
                    {article.highlights.map((h, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <div className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0 mt-0.5">
                          <span className="text-xs font-bold">{idx + 1}</span>
                        </div>
                        <span className="text-sm text-foreground-700">{h}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Gallery */}
                <div className="bg-background-100 rounded-xl p-6 mb-6">
                  <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">精彩剧照</h2>
                  <div className="grid grid-cols-2 gap-3">
                    {article.gallery.map((img, idx) => (
                      <div key={idx} className="rounded-lg overflow-hidden aspect-video bg-background-200">
                        <img
                          src={img}
                          alt={`${article.title} 剧照 ${idx + 1}`}
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-400"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <aside className="lg:col-span-1">
                {/* Related recommendations */}
                <div className="bg-background-100 rounded-xl p-6 sticky top-24">
                  <h3 className="text-base font-heading font-bold text-foreground-950 mb-4">
                    相关推荐
                  </h3>
                  <div className="space-y-4">
                    {related.map((item) => (
                      <Link
                        key={item.id}
                        to={`/article/${item.id}`}
                        className="flex gap-3 group cursor-pointer"
                      >
                        <div className="w-16 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-background-200">
                          <img
                            src={item.image}
                            alt={item.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 mb-1">
                            {item.title}
                          </h4>
                          <div className="flex items-center gap-2">
                            <span className="flex items-center gap-0.5 text-accent-500">
                              <i className="ri-star-fill text-xs"></i>
                              <span className="text-xs font-medium">{item.rating}</span>
                            </span>
                            <span className="text-xs text-foreground-400">{item.year}</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}